import { useState, useMemo } from 'react'

import { useDidMountEffect } from 'hooks'


type UsePaginationParams = {
  /** Page selected on initial render, defaults to 1 */
  initialPage?: number

  /** Page selected on initial render, defaults to 1 */
  limit?: number

  /** Total amount of items */
  total?: number

  /** Total amount of pages */
  count?: number

  onChange?: (pagination: UsePaginationEvent) => void
}

type UsePaginationResult = {
  page: number
  limit: number
  offset: number
  total: number
  count: number
  isHasMorePages: boolean
  setPage: (page: number) => void
  next: () => void
  previous: () => void
  first: () => void
  last: () => void
}

export type UsePaginationEvent = {
  page: number
  limit: number
  offset: number
  total: number
  count: number
}

export const usePagination = ({
  initialPage = 1,
  limit = 10,
  total = 0,
  onChange,
}: UsePaginationParams): UsePaginationResult => {
  const [activePage, setActivePage] = useState(initialPage)

  const { lastPage } = useMemo(() => {
    return {
      lastPage: Math.ceil(total / limit),
    }
  }, [limit, total])

  useDidMountEffect(() => {
    let page = activePage

    if (page > lastPage) {
      page = 1
    }

    const offset = getOffset(page)
    setActivePage(page)

    onChange?.({
      page,
      limit,
      offset,
      total,
      count: lastPage,
    })
  }, [limit, total])

  const setPage = (value: number) => {
    let page

    if (value <= 0) {
      page = 1
    } else if (value > lastPage) {
      page = lastPage
    } else {
      page = value
    }

    setActivePage(page)

    const offset = getOffset(page)

    onChange?.({
      page,
      limit,
      offset,
      total,
      count: lastPage,
    })
  }

  const getOffset = (page: number) => {
    if (page === 1) {
      return 0
    } else {
      return (page - 1) * limit
    }
  }

  const next = () => setPage(activePage + 1)
  const previous = () => setPage(activePage - 1)
  const first = () => setPage(1)
  const last = () => setPage(lastPage)

  const isHasMorePages = activePage < lastPage

  return {
    page: activePage,
    limit,
    offset: getOffset(activePage),
    total,
    count: lastPage,
    isHasMorePages,
    setPage,
    next,
    previous,
    first,
    last,
  }
}
